import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import { api } from "@shared/routes";
import type { SavDossier, SavIntervention, SavPiece } from "@shared/schema";

export function useSavDossiers() {
  return useQuery<SavDossier[]>({
    queryKey: [api.sav.list.path],
    queryFn: async () => {
      const res = await authFetch(api.sav.list.path);
      if (!res.ok) throw new Error("Failed to fetch SAV dossiers");
      return res.json();
    },
  });
}

export function useSavDossier(id: string | null) {
  return useQuery<SavDossier>({
    queryKey: [api.sav.list.path, id],
    queryFn: async () => {
      const res = await authFetch(`/api/sav/${id}`);
      if (!res.ok) throw new Error("Failed to fetch SAV dossier");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateSavDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: any) => {
      const res = await authFetch(api.sav.create.path, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create SAV dossier");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [api.sav.list.path] });
    },
  });
}

export function useUpdateSavDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      const res = await authFetch(`/api/sav/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update SAV dossier");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [api.sav.list.path] });
    },
  });
}

export function useDeleteSavDossier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/sav/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete SAV dossier");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [api.sav.list.path] });
    },
  });
}

export function useSavInterventions(dossierId: string | null) {
  return useQuery<SavIntervention[]>({
    queryKey: [api.sav.list.path, dossierId, "interventions"],
    queryFn: async () => {
      const res = await authFetch(`/api/sav/${dossierId}/interventions`);
      if (!res.ok) throw new Error("Failed to fetch interventions");
      return res.json();
    },
    enabled: !!dossierId,
  });
}

export function useCreateSavIntervention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dossierId, ...data }: any) => {
      const res = await authFetch(`/api/sav/${dossierId}/interventions`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.message || "Échec de l'envoi du suivi");
      return res.json();
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [api.sav.list.path, vars.dossierId, "interventions"] });
    },
  });
}

export function useUpdateSavIntervention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dossierId, id, description }: { dossierId: string; id: string; description: string }) => {
      const res = await authFetch(`/api/sav/${dossierId}/interventions/${id}`, {
        method: "PATCH", body: JSON.stringify({ description }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.message || "Modification impossible");
      return res.json();
    },
    onSuccess: (_result, { dossierId }) => qc.invalidateQueries({ queryKey: [api.sav.list.path, dossierId, "interventions"] }),
  });
}

export function useDeleteSavIntervention() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dossierId, id }: { dossierId: string; id: string }) => {
      const res = await authFetch(`/api/sav/${dossierId}/interventions/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json().catch(() => null))?.message || "Suppression impossible");
    },
    onSuccess: (_result, { dossierId }) => {
      qc.invalidateQueries({ queryKey: [api.sav.list.path, dossierId, "interventions"] });
      qc.invalidateQueries({ queryKey: [api.sav.list.path, dossierId, "photos-signed"] });
      qc.invalidateQueries({ queryKey: [api.sav.list.path] });
    },
  });
}

export function useSavPieces(dossierId: string | null) {
  return useQuery<SavPiece[]>({
    queryKey: [api.sav.list.path, dossierId, "pieces"],
    queryFn: async () => {
      const res = await authFetch(`/api/sav/${dossierId}/pieces`);
      if (!res.ok) throw new Error("Failed to fetch pieces");
      return res.json();
    },
    enabled: !!dossierId,
  });
}

export function useCreateSavPiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dossierId, ...data }: any) => {
      const res = await authFetch(`/api/sav/${dossierId}/pieces`, {
        method: "POST",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create piece");
      return res.json();
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: [api.sav.list.path, vars.dossierId, "pieces"] });
    },
  });
}

export function useUpdateSavPiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: any) => {
      const res = await authFetch(`/api/sav/pieces/${id}`, {
        method: "PUT",
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update piece");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [api.sav.list.path] });
    },
  });
}

export function useDeleteSavPiece() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await authFetch(`/api/sav/pieces/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete piece");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [api.sav.list.path] });
    },
  });
}
